// bidNoFill.js — "demand wasted" detector: requests where Prebid, Amazon APS or
// SpringServe returned a real bid, yet the ad server came back with NO FILL.
//
// This is the highest-value revenue leak an AdOps reviewer hunts for. A no-fill on
// a slot nobody bid on is just empty inventory. A no-fill on a slot that HAD paid
// demand sitting in front of it is a misconfiguration — a missing/misprized line
// item, a broken hb_pb price bucket, a targeting mismatch, a key-value that never
// made it into the request. It is invisible in the existing panels because
// inventoryFlow's `nofill` bucket and bidHealth's `noFill` counter both stop at
// "GAM returned nothing" and never ask whether demand was present.
//
// Covers both channels:
//   display — one item per GAM slot lifecycle (buildSlotLifecycles)
//   video   — one item per video session (buildVideoSessions): GAM VAST, DAI/SSAI
//             streams and FreeWheel, where SpringServe is the observable demand.
//
// Conservative by design: an item is only flagged on an EXPLICIT no-fill signal
// (creativeSource 'no-fill' / validData.noFill / VAST filled===false / VAST error
// 303) AND an explicit bid signal. An unparsed or uncaptured response is never
// treated as a no-fill — a false "you're losing money" flag destroys trust faster
// than a missed one.
//
// Pure function — no React, no imports. Safe from the dashboard, the sign-off
// report, or a headless test.

const SRC_LABEL = { prebid: 'Prebid', aps: 'Amazon APS', springserve: 'SpringServe' };

/** VAST error 3xx code meaning "no ads returned" — a no-fill on the wire. */
const VAST_NO_ADS = 303;

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

// ── Display ──────────────────────────────────────────────────────────────────

/** Explicit GAM no-fill: the proxy either parsed an empty ad_type or said so. */
function displayNoFill(lc) {
  const gamRow = lc?.phases?.gam?.row;
  if (gamRow?.validData?.noFill === true) return 'GAM returned no fill';
  const src = gamRow?.validData?.expanded?.creativeSource;
  if (src?.source === 'no-fill') return src.label || 'GAM returned no fill';
  return null;
}

/**
 * Prebid demand for this slot: a bid at a real price. Mirrors inventoryFlow's
 * prebidBidBack() but also reports WHO bid and at what CPM so the flag is
 * actionable ("$4.20 from ttd went unfilled") rather than just a count.
 */
function prebidDemand(lc) {
  const p = lc?.phases?.prebid;
  if (!p?.fired) return null;
  const bidders = [];
  let top = null;
  const consider = (seat, price) => {
    const cpm = num(price);
    if (cpm == null || cpm <= 0) return;
    if (seat) bidders.push(seat);
    if (top == null || cpm > top.cpm) top = { seat: seat || null, cpm };
  };
  for (const b of Array.isArray(p.bids) ? p.bids : []) {
    const status = String(b.status || '').toLowerCase();
    if (status === 'bid' || status === 'won') consider(b.seat || b.bidder, b.price);
  }
  if (p.winner) consider(p.winner.seat || p.winner.bidder, p.winner.price);
  if (!top) return null;
  return { top, bidders: [...new Set(bidders)] };
}

/**
 * APS demand. Two independent proofs, either is sufficient:
 *   • the parsed APS response carried bid tokens   (bidStatus === 'bid')
 *   • amzn_b / amznslots reached the GAM request   (passedToGAM)
 * APS never exposes a CPM on the wire, so there is no cpm to report.
 */
function apsDemand(lc) {
  const a = lc?.phases?.amazon;
  if (!a) return null;
  if (a.bidStatus === 'bid' || a.passedToGAM === true) {
    return { passedToGAM: a.passedToGAM === true };
  }
  return null;
}

function buildDisplayItems(lifecycles) {
  const items = [];
  let withDemand = 0;
  for (const lc of lifecycles || []) {
    const pb = prebidDemand(lc);
    const aps = apsDemand(lc);
    if (!pb && !aps) continue;
    withDemand += 1;

    const reason = displayNoFill(lc);
    if (!reason) continue;

    const sources = [];
    if (pb) sources.push('prebid');
    if (aps) sources.push('aps');

    // Did the bid actually reach the ad server? A bid that never made it into
    // cust_params is a different (upstream) bug than a bid GAM saw and ignored.
    const passedToGAM = !!(lc?.phases?.gam?.hbPb || lc?.phases?.gam?.cacheId ||
      lc?.phases?.gam?.hbBidder || aps?.passedToGAM);

    items.push({
      channel: 'display',
      key: lc.key ?? null,
      rowKey: lc?.phases?.gam?.rowKey ?? null,
      adUnit: lc.adUnit || '(unknown)',
      timestamp: lc.timestamp ?? null,
      sources,
      bidders: pb ? pb.bidders : [],
      bidCpm: pb ? pb.top.cpm : null,
      topBidder: pb ? pb.top.seat : null,
      passedToGAM,
      reason,
      detail: passedToGAM
        ? 'Bid reached GAM in cust_params but the ad server returned no creative — check line-item targeting / price buckets.'
        : 'Bid returned to the SDK but never reached GAM, and the slot went unfilled — check the ad-request build.',
    });
  }
  return { items, withDemand };
}

// ── Video ────────────────────────────────────────────────────────────────────

/** Any VAST error beacon in this session that means "no ads". */
function hasNoAdsError(errorRows) {
  return (errorRows || []).some((r) => Number(r?.validData?.errorCode) === VAST_NO_ADS);
}

/** Which video demand partners returned a bid for this session. */
function videoDemandSources(s) {
  const sources = [];
  if (s?.bids?.ss?.status === 'bid') sources.push('springserve');
  if (s?.bids?.amazon?.status === 'bid') sources.push('aps');
  // FreeWheel sessions have no bids block — the observable upstream is the
  // adjacent SpringServe bid attached as videoDemand (timing-inferred link).
  if (s?.videoDemand?.partner === 'springserve' && s.videoDemand.status === 'bid') {
    if (!sources.includes('springserve')) sources.push('springserve');
  }
  return sources;
}

/** Explicit no-fill signal for a video session, or null. */
function videoNoFill(s) {
  if (s?.sessionType === 'dai_stream') {
    const breaks = Array.isArray(s.adBreaks) ? s.adBreaks : [];
    const empty = breaks.filter((b) => b.source === 'vast' && b.filled === false);
    if (empty.length) {
      return `${empty.length} ad break${empty.length === 1 ? '' : 's'}: no fill from SpringServe`;
    }
    if (hasNoAdsError(s.vastErrors)) return 'VAST 303 — no ads returned';
    // A break that formed but reported no-ads inside its own error beacons.
    if (breaks.some((b) => hasNoAdsError(b.errors))) return 'VAST 303 — no ads returned';
    return null;
  }
  // 'vast' sessions — GAM VAST and FreeWheel.
  if (s?.vast?.filled === false) return 'SpringServe VAST: no fill';
  if (s?.gamFilled === false) return 'Video ad server returned an empty VAST';
  if (hasNoAdsError(s?.tracking?.errors)) return 'VAST 303 — no ads returned';
  return null;
}

function buildVideoItems(sessions) {
  const items = [];
  let withDemand = 0;
  for (const s of sessions || []) {
    const sources = videoDemandSources(s);
    if (!sources.length) continue;
    withDemand += 1;

    const reason = videoNoFill(s);
    if (!reason) continue;

    items.push({
      channel: 'video',
      key: s._k ?? null,
      rowKey: s._k ?? null,
      adUnit: s.adUnit || s.displayTitle || s.streamId || '(video session)',
      timestamp: s.timestamp ?? null,
      sessionType: s.flavor || s.sessionType || 'vast',
      sources,
      bidders: [],
      bidCpm: null,
      topBidder: null,
      // Video bids are forwarded as cust_params on the ad request; the video
      // lifecycle already computes whether that forwarding happened.
      passedToGAM: s?.bids ? s.bids.tokensOk !== false : null,
      reason,
      detail: s?.bids?.tokensOk === false
        ? 'A video bid returned but was NOT forwarded to the ad request — the ad server never saw it.'
        : 'Video demand bid but the ad server returned an empty VAST — check video line items / creative eligibility.',
    });
  }
  return { items, withDemand };
}

// ── Public API ───────────────────────────────────────────────────────────────

/**
 * @param {Array}  lifecycles     buildSlotLifecycles() output (display slots)
 * @param {Array}  videoSessions  buildVideoSessions() output
 * @returns rollup of requests that had demand but came back unfilled
 */
export function computeBidNoFill(lifecycles = [], videoSessions = []) {
  const d = buildDisplayItems(lifecycles);
  const v = buildVideoItems(videoSessions);
  const items = [...d.items, ...v.items].sort((a, b) => (b.bidCpm || 0) - (a.bidCpm || 0) || (b.timestamp || 0) - (a.timestamp || 0));

  const bySource = { prebid: 0, aps: 0, springserve: 0 };
  const byUnit = new Map();
  let cpmAtRisk = 0;
  let notPassed = 0;

  for (const it of items) {
    for (const s of it.sources) if (bySource[s] != null) bySource[s] += 1;
    if (it.bidCpm) cpmAtRisk += it.bidCpm;
    if (it.passedToGAM === false) notPassed += 1;
    const u = byUnit.get(it.adUnit) || { adUnit: it.adUnit, count: 0, cpmAtRisk: 0, sources: new Set() };
    u.count += 1;
    u.cpmAtRisk += it.bidCpm || 0;
    it.sources.forEach((s) => u.sources.add(s));
    byUnit.set(it.adUnit, u);
  }

  const withDemand = d.withDemand + v.withDemand;
  const count = items.length;
  const rate = withDemand > 0 ? Math.round((count / withDemand) * 100) : 0;

  // ── Severity ──────────────────────────────────────────────────────────────
  // Any bid that never reached the ad server is a build bug, not an auction
  // outcome — that fails outright. Otherwise the rate decides.
  let severity = 'pass';
  let headline = withDemand === 0
    ? 'No Prebid / APS / SpringServe bids captured yet'
    : `All ${withDemand} request${withDemand === 1 ? '' : 's'} with demand filled`;

  if (count > 0) {
    const srcList = Object.entries(bySource).filter(([, n]) => n > 0).map(([s, n]) => `${SRC_LABEL[s]} ${n}`).join(' · ');
    headline = `${count} of ${withDemand} request${withDemand === 1 ? '' : 's'} with demand came back UNFILLED — ${srcList}`;
    severity = notPassed > 0 || rate >= 25 ? 'fail' : rate >= 10 ? 'warn' : 'info';
  }

  return {
    count,
    withDemand,
    rate,
    bySource,
    byChannel: { display: d.items.length, video: v.items.length },
    cpmAtRisk: Math.round(cpmAtRisk * 100) / 100,
    notPassedToAdServer: notPassed,
    items,
    units: [...byUnit.values()]
      .map((u) => ({ ...u, sources: [...u.sources], cpmAtRisk: Math.round(u.cpmAtRisk * 100) / 100 }))
      .sort((a, b) => b.cpmAtRisk - a.cpmAtRisk || b.count - a.count),
    severity,
    headline,
  };
}

export { SRC_LABEL };
export default computeBidNoFill;
